export type Operator = '+' | '−' | '×' | '÷' | '';

export interface Cage {
  indexes: number[];
  result: number;
  operator: Operator;
}

export interface Puzzle {
  size: number;
  cages: Cage[];
  solution: number[];
}

const TIMES: Operator = '×';
const PLUS: Operator = '+';
const MINUS: Operator = '−';
const DIV: Operator = '÷';

const OP_MAP: Record<string, Operator> = { '+': PLUS, '-': MINUS, '*': TIMES, ':': DIV };

function shuffleArray<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = a[i]!;
    a[i] = a[j]!;
    a[j] = tmp;
  }
  return a;
}

function generateLatinSquare(size: number): number[] {
  const grid: number[] = new Array(size * size).fill(0);
  const values = Array.from({ length: size }, (_, i) => i + 1);

  function tryFill(idx: number): boolean {
    if (idx === size * size) return true;
    const row = Math.floor(idx / size);
    const col = idx % size;
    const used = new Set<number>();
    for (let c = 0; c < col; c++) used.add(grid[row * size + c]!);
    for (let r = 0; r < row; r++) used.add(grid[r * size + col]!);
    const candidates = shuffleArray(values.filter((n) => !used.has(n)));
    for (const v of candidates) {
      grid[idx] = v;
      if (tryFill(idx + 1)) return true;
    }
    grid[idx] = 0;
    return false;
  }

  tryFill(0);
  return grid;
}

function tryExpand(cagesIndexes: number[], newCage: number[], startId: number, size: number, attempt: number): void {
  while (newCage.length < size && attempt < 5) {
    const direction = Math.floor(Math.random() * 4);
    attempt += 1;
    let nextId = -1;
    if (direction === 0 && startId >= size && cagesIndexes[startId - size] === -1) nextId = startId - size;
    else if (direction === 1 && startId + size < size * size && cagesIndexes[startId + size] === -1) nextId = startId + size;
    else if (direction === 2 && startId % size !== 0 && cagesIndexes[startId - 1] === -1) nextId = startId - 1;
    else if (direction === 3 && startId % size !== size - 1 && cagesIndexes[startId + 1] === -1) nextId = startId + 1;
    if (nextId >= 0) {
      cagesIndexes[nextId] = startId;
      newCage.push(nextId);
      tryExpand(cagesIndexes, newCage, nextId, size, attempt + 1);
    }
  }
}

export function computeResult(values: number[], indexes: number[], operator: Operator): number {
  if (operator === '') return values[indexes[0]!]!;
  if (operator === TIMES) {
    let r = 1;
    for (const i of indexes) r *= values[i]!;
    return r;
  }
  if (operator === PLUS) {
    let r = 0;
    for (const i of indexes) r += values[i]!;
    return r;
  }
  if (operator === MINUS) {
    let r = 0;
    for (const i of indexes) {
      const v = values[i]!;
      if (r === 0) r = v;
      else if (r > v) r -= v;
      else r = v - r;
    }
    return r;
  }
  // DIV
  let r = 0;
  for (const i of indexes) {
    const v = values[i]!;
    if (r === 0) r = v;
    else if (r > v) r = r / v;
    else r = v / r;
  }
  return r;
}

function isIntegerRatio(a: number, b: number): boolean {
  return a % b === 0 || b % a === 0;
}

function pickWeighted(ops: Partial<Record<Operator, number>>): Operator {
  const keys = Object.keys(ops) as Operator[];
  if (keys.length === 0) return PLUS;
  const total = keys.reduce((acc, k) => acc + (ops[k] ?? 0), 0);
  const rand = Math.random() * total;
  let sum = 0;
  for (const k of keys) {
    sum += ops[k] ?? 0;
    if (sum > rand) return k;
  }
  return keys[keys.length - 1]!;
}

export function generatePuzzle(size: number, allowedOpsRaw: string[]): Puzzle {
  const allowedOps = allowedOpsRaw.map((o) => OP_MAP[o] ?? (o as Operator));

  const level = generateLatinSquare(size);
  const cagesIndexes: number[] = new Array(size * size).fill(-1);
  const cages: Cage[] = [];

  for (let id = 0; id < size * size; id++) {
    if (cagesIndexes[id] === -1) {
      const newCage = [id];
      cagesIndexes[id] = id;
      tryExpand(cagesIndexes, newCage, id, size, 1);
      cages.push({ indexes: newCage, result: -1, operator: '' });
    }
  }

  for (const cage of cages) {
    if (cage.indexes.length === 1) {
      cage.operator = '';
      cage.result = level[cage.indexes[0]!]!;
      continue;
    }
    if (cage.indexes.length === 2) {
      const probs: Partial<Record<Operator, number>> = { [MINUS]: 40, [DIV]: 35, [TIMES]: 25, [PLUS]: 20 };
      const a = level[cage.indexes[0]!]!;
      const b = level[cage.indexes[1]!]!;
      if (!isIntegerRatio(a, b)) delete probs[DIV];
      const filtered: Partial<Record<Operator, number>> = {};
      for (const op of allowedOps) {
        if (probs[op] !== undefined) filtered[op] = probs[op];
      }
      const op = pickWeighted(filtered);
      cage.operator = op;
      cage.result = computeResult(level, cage.indexes, op);
      continue;
    }
    // 3+
    const probs: Partial<Record<Operator, number>> = { [TIMES]: 40, [PLUS]: 60 };
    const filtered: Partial<Record<Operator, number>> = {};
    for (const op of allowedOps) {
      if (probs[op] !== undefined) filtered[op] = probs[op];
    }
    const op = pickWeighted(filtered);
    cage.operator = op;
    cage.result = computeResult(level, cage.indexes, op);
  }

  return { size, cages, solution: level };
}
